import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Settings,
  Zap,
  Database,
  Cpu,
} from 'lucide-react';
import { MONAITrainingConfig, MONAIBundle } from '@/mock/gpu-instance';

interface MONAIConfigBuilderProps {
  config: MONAITrainingConfig;
  onChange: (config: MONAITrainingConfig) => void;
  selectedBundle?: MONAIBundle;
}

const AUGMENTATION_OPTIONS = [
  { id: 'RandFlipd', name: 'Random Flip', description: 'Random axis flip' },
  { id: 'RandRotate90d', name: 'Random Rotate 90°', description: 'Random 90° rotation' },
  { id: 'RandScaleIntensityd', name: 'Scale Intensity', description: 'Random intensity scaling' },
  { id: 'RandShiftIntensityd', name: 'Shift Intensity', description: 'Random intensity shift' },
  { id: 'RandGaussianNoised', name: 'Gaussian Noise', description: 'Add random noise' },
  { id: 'RandGaussianSmoothd', name: 'Gaussian Smooth', description: 'Random smoothing' },
  { id: 'RandAdjustContrastd', name: 'Adjust Contrast', description: 'Random contrast adjustment' },
  { id: 'RandCropByPosNegLabeld', name: 'Crop by Label', description: 'Random crop around labels' },
  { id: 'RandZoomd', name: 'Random Zoom', description: 'Random zoom in/out' },
  { id: 'RandAffined', name: 'Affine Transform', description: 'Random affine transformation' },
];

const MONAIConfigBuilder = ({
  config,
  onChange,
  selectedBundle,
}: MONAIConfigBuilderProps) => {
  const handleChange = <K extends keyof MONAITrainingConfig>(
    key: K,
    value: MONAITrainingConfig[K]
  ) => {
    onChange({ ...config, [key]: value });
  };

  const toggleAugmentation = (augId: string) => {
    const newAugs = config.augmentations.includes(augId)
      ? config.augmentations.filter((a) => a !== augId)
      : [...config.augmentations, augId];
    handleChange('augmentations', newAugs);
  };

  return (
    <div className="space-y-6">
      {/* Training Parameters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Training Parameters
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Epochs</Label>
              <Input
                type="number"
                value={config.epochs}
                onChange={(e) => handleChange('epochs', parseInt(e.target.value) || 1)}
                min={1}
                max={1000}
              />
            </div>
            <div className="space-y-2">
              <Label>Batch Size</Label>
              <Input
                type="number"
                value={config.batchSize}
                onChange={(e) => handleChange('batchSize', parseInt(e.target.value) || 1)}
                min={1}
                max={128}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Learning Rate</Label>
              <Input
                type="number"
                value={config.learningRate}
                onChange={(e) => handleChange('learningRate', parseFloat(e.target.value) || 0.0001)}
                step="0.0001"
                min={0.000001}
                max={1}
              />
            </div>
            <div className="space-y-2">
              <Label>Num Workers</Label>
              <Input
                type="number"
                value={config.numWorkers}
                onChange={(e) => handleChange('numWorkers', parseInt(e.target.value) || 0)}
                min={0}
                max={32}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Optimizer</Label>
              <Select
                value={config.optimizer}
                onValueChange={(v) => handleChange('optimizer', v as MONAITrainingConfig['optimizer'])}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="adam">Adam</SelectItem>
                  <SelectItem value="adamw">AdamW</SelectItem>
                  <SelectItem value="sgd">SGD</SelectItem>
                  <SelectItem value="novograd">Novograd</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Scheduler</Label>
              <Select
                value={config.scheduler}
                onValueChange={(v) => handleChange('scheduler', v as MONAITrainingConfig['scheduler'])}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cosine">Cosine Annealing</SelectItem>
                  <SelectItem value="warmup_cosine">Warmup + Cosine</SelectItem>
                  <SelectItem value="step">Step LR</SelectItem>
                  <SelectItem value="polynomial">Polynomial</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Performance Options */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Zap className="h-4 w-4" />
            Performance Options
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label>Mixed Precision (AMP)</Label>
              <p className="text-sm text-muted-foreground">Use FP16 for faster training</p>
            </div>
            <Switch
              checked={config.mixedPrecision}
              onCheckedChange={(v) => handleChange('mixedPrecision', v)}
            />
          </div>

          <div className="space-y-2">
            <Label>Caching Strategy</Label>
            <Select
              value={config.cachingStrategy}
              onValueChange={(v) => handleChange('cachingStrategy', v as MONAITrainingConfig['cachingStrategy'])}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No Caching</SelectItem>
                <SelectItem value="memory">In-Memory Cache</SelectItem>
                <SelectItem value="persistent">Persistent Cache</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {config.cachingStrategy === 'none' && 'Load data from disk each epoch'}
              {config.cachingStrategy === 'memory' && 'Cache preprocessed data in RAM (requires more memory)'}
              {config.cachingStrategy === 'persistent' && 'Cache to disk for faster subsequent epochs'}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Data Augmentation */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Database className="h-4 w-4" />
            Data Augmentation
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-2">
            {AUGMENTATION_OPTIONS.map((aug) => (
              <div
                key={aug.id}
                className={`p-3 rounded-lg border cursor-pointer transition-all ${
                  config.augmentations.includes(aug.id)
                    ? 'border-primary bg-primary/5'
                    : 'border-muted hover:border-muted-foreground/30'
                }`}
                onClick={() => toggleAugmentation(aug.id)}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-sm">{aug.name}</span>
                  {config.augmentations.includes(aug.id) && (
                    <Badge variant="default" className="h-5">ON</Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-1">{aug.description}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            Selected: {config.augmentations.length} augmentation{config.augmentations.length !== 1 ? 's' : ''}
          </p>
        </CardContent>
      </Card>

      {/* Resource Estimate */}
      {selectedBundle && (
        <Card className="bg-muted/30">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-3">
              <Cpu className="h-4 w-4" />
              <span className="font-medium text-sm">Estimated Resources</span>
            </div>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-xs text-muted-foreground">VRAM Required</div>
                <div className="font-mono font-bold">~{selectedBundle.vramRequired * (config.mixedPrecision ? 0.6 : 1)} GB</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Batch Memory</div>
                <div className="font-mono font-bold">~{(config.batchSize * 0.5).toFixed(1)} GB</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Total Est.</div>
                <div className="font-mono font-bold text-primary">
                  ~{Math.ceil(selectedBundle.vramRequired * (config.mixedPrecision ? 0.6 : 1) + config.batchSize * 0.5)} GB
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default MONAIConfigBuilder;
